// Clients belong to a real signed-in workspace lifetime, outside Activity's
// disconnected effects. Views share one session client and input queue.
import type { TerminalClientFactory, LiveTerminalClient } from '../api/terminal'
import type { BoardStore } from '../store/store'

export class TerminalClients {
  private identity: string | null
  private readonly clients = new Map<string, { client: LiveTerminalClient; uses: number }>()
  constructor(private readonly store: BoardStore, private readonly factory: TerminalClientFactory) {
    this.identity = this.currentIdentity()
  }
  private currentIdentity(): string | null {
    const { me, status } = this.store.getState()
    return status !== 'signed_out' && me?.member ? JSON.stringify([me.workspace.id, me.member.id]) : null
  }
  refresh = (): void => {
    const identity = this.currentIdentity()
    if (identity === this.identity) return
    this.dispose()
    this.identity = identity
  }
  get(session: string): LiveTerminalClient | null {
    this.refresh()
    const { me } = this.store.getState()
    if (!this.identity || !me?.member) return null
    let held = this.clients.get(session)
    if (!held) {
      const identity = this.identity
      const client = this.factory({
        workspace: me.workspace.id, member: me.member.id, session,
        current: () => this.currentIdentity() === identity,
        online: () => !this.store.getState().offline,
        unauthorized: () => { void this.store.refreshSession() },
      })
      held = {client, uses: 0}
      this.clients.set(session, held)
    }
    return held.client
  }
  retain(client: LiveTerminalClient): () => void {
    const held = [...this.clients.entries()].find(([, value]) => value.client === client)
    if (!held) return () => {}
    const [session, value] = held
    value.uses += 1
    return () => {
      value.uses -= 1
      if (value.uses === 0) queueMicrotask(() => {
        // A keyed reload renders with this client before the old insertion
        // cleanup releases it. Let the same commit retain it again first.
        if (value.uses === 0 && this.clients.get(session) === value) {
          this.clients.delete(session)
          value.client.dispose()
        }
      })
    }
  }
  dispose(): void {
    for (const { client } of this.clients.values()) client.dispose()
    this.clients.clear()
  }
}
